import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Paper,
  Link,
} from "@material-ui/core";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const PURPOSES = ["recordatorio", "confirmada", "modificada", "cancelada"];

const INSTRUCTIONS = {
  recordatorio: {
    title: "Plantilla de Recordatorio",
    suggestion:
      "Hola {{1}}, este es un recordatorio de tu cita con {{2}} el día {{3}} a las {{4}}.\nSi necesitas reprogramar o cancelar, por favor responde a este mensaje.",
    description: "Se usa para recordar al paciente su cita.",
    variables: [
      { key: "{{1}}", meaning: "Paciente (nombre)" },
      { key: "{{2}}", meaning: "Doctor / profesional" },
      { key: "{{3}}", meaning: "Fecha" },
      { key: "{{4}}", meaning: "Hora" }
    ],
    sampleData: {
      "{{1}}": "María Pérez",
      "{{2}}": "Dr. Gómez",
      "{{3}}": "2026-02-10",
      "{{4}}": "15:30"
    },
    suggestedTemplateName: "clinic_chat_recordatorio"
  },
  confirmada: {
    title: "Plantilla de Cita Confirmada",
    suggestion:
      "Hola {{1}}, se ha agendado una nueva cita.\n\n👨‍⚕ Atenderá: {{2}}\n📅 Fecha: {{3}}\n🕒 Horario: {{4}} a {{5}}\n\n📅 Agrega la cita a Google Calendar: {{6}}.\nPor favor, llega 10 minutos antes. ¡Gracias!",
    description: "Se usa cuando se agenda una nueva cita.",
    variables: [
      { key: "{{1}}", meaning: "Paciente (nombre)" },
      { key: "{{2}}", meaning: "Doctor / profesional" },
      { key: "{{3}}", meaning: "Fecha" },
      { key: "{{4}}", meaning: "Hora inicio" },
      { key: "{{5}}", meaning: "Hora fin" },
      { key: "{{6}}", meaning: "Link a Google Calendar" }
    ],
    sampleData: {
      "{{1}}": "Juan López",
      "{{2}}": "Dra. Ruiz",
      "{{3}}": "2026-02-12",
      "{{4}}": "09:00",
      "{{5}}": "09:30",
      "{{6}}": "https://ejemplo.com/cita/12345"
    },
    suggestedTemplateName: "clinic_chat_cita_confirmada"
  },
  modificada: {
    title: "Plantilla de Cita Modificada",
    suggestion:
      "🔄 *Cita modificada*\n\nHola {{1}},\n👨‍⚕️ {{2}}\n📍 Nueva fecha: {{3}}\n🕒 Nueva hora: {{4}} - {{5}}\n📅 Agrega la cita a Google Calendar: {{6}}.\nPor favor, llega 10 minutos antes.",
    description: "Se usa cuando la cita fue modificada.",
    variables: [
      { key: "{{1}}", meaning: "Paciente (nombre)" },
      { key: "{{2}}", meaning: "Doctor / profesional" },
      { key: "{{3}}", meaning: "Nueva fecha" },
      { key: "{{4}}", meaning: "Nueva hora inicio" },
      { key: "{{5}}", meaning: "Nueva hora fin" },
      { key: "{{6}}", meaning: "Link a Google Calendar" }
    ],
    sampleData: {
      "{{1}}": "Lucía Fernández",
      "{{2}}": "Dr. Pérez",
      "{{3}}": "2026-02-15",
      "{{4}}": "11:00",
      "{{5}}": "11:30",
      "{{6}}": "https://ejemplo.com/cita/67890"
    },
    suggestedTemplateName: "clinic_chat_cita_modificada"
  },
  cancelada: {
    title: "Plantilla de Cita Cancelada",
    suggestion:
      "❌ *Cita cancelada*\n\nHola {{1}},\n👨‍⚕️ {{2}}\n📍 Fecha: {{3}}\n🕒 Hora: {{4}} - {{5}}\n⚠️ Si agregaste la cita a tu calendario anteriormente, elimínala.\nGracias por tu comprensión.",
    description: "Se usa cuando la cita fue cancelada.",
    variables: [
      { key: "{{1}}", meaning: "Paciente (nombre)" },
      { key: "{{2}}", meaning: "Doctor / profesional" },
      { key: "{{3}}", meaning: "Fecha" },
      { key: "{{4}}", meaning: "Hora inicio" },
      { key: "{{5}}", meaning: "Hora fin" }
    ],
    sampleData: {
      "{{1}}": "Martín Díaz",
      "{{2}}": "Dra. Sánchez",
      "{{3}}": "2026-02-20",
      "{{4}}": "14:00",
      "{{5}}": "14:30"
    },
    suggestedTemplateName: "clinic_chat_cita_cancelada"
  }
};

const TWILIO_TEMPLATE_BUILDER_URL =
  "https://console.twilio.com/us1/develop/sms/content-template-builder";

const TwilioTemplateWizard = ({ accountSid, onFinish }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [hxTemplate, setHxTemplate] = useState("");
  const [loading, setLoading] = useState(false);
  const [templatesMap, setTemplatesMap] = useState({});
  const [savedSteps, setSavedSteps] = useState({});

  useEffect(() => {
    const fetchAll = async () => {
      if (!accountSid) {
        setTemplatesMap({});
        setHxTemplate("");
        return;
      }
      setLoading(true);
      try {
        const map = {};
        await Promise.all(
          PURPOSES.map(async (purpose) => {
            try {
              const { data } = await api.get(`/twilio-templates/${accountSid}/${purpose}`);
              map[purpose] = data || null;
            } catch (err) {
              map[purpose] = null;
            }
          })
        );
        setTemplatesMap(map);
      } catch (err) {
        // ignore
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [accountSid]);

  useEffect(() => {
    const tpl = templatesMap[PURPOSES[activeStep]] || null;
    setHxTemplate(tpl?.hx_template || "");
  }, [activeStep, templatesMap]);

  const currentPurpose = PURPOSES[activeStep];
  const meta = INSTRUCTIONS[currentPurpose];

  const handleSave = async () => {
    if (!accountSid) return;
    setLoading(true);
    try {
      const existing = templatesMap[currentPurpose];
      if (existing?.id) {
        const { data } = await api.put("/twilio-templates/" + existing.id, {
          accountSid,
          purpose: currentPurpose,
          hx_template: hxTemplate
        });
        setTemplatesMap((prev) => ({ ...prev, [currentPurpose]: data }));
      } else {
        const { data } = await api.post("/twilio-templates", {
          accountSid,
          purpose: currentPurpose,
          hx_template: hxTemplate
        });
        setTemplatesMap((prev) => ({ ...prev, [currentPurpose]: data }));
      }

      toast.success("Plantilla guardada correctamente");
      setSavedSteps((prev) => ({ ...prev, [currentPurpose]: true }));

      if (activeStep < PURPOSES.length - 1) {
        setActiveStep((prev) => prev + 1);
      } else {
        toast.success("Todas las plantillas configuradas para esta cuenta");
        if (onFinish) onFinish();
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) setActiveStep((prev) => prev - 1);
  };

  const allTemplatesDefined = PURPOSES.every((p) => {
    const tpl = templatesMap[p];
    if (tpl && tpl.hx_template && tpl.hx_template.trim().length > 0) return true;
    if (savedSteps[p]) return true;
    return false;
  });

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Configurar Plantillas de Twilio ({accountSid ? `${accountSid.substring(0,4)}...${accountSid.substring(accountSid.length-4)}` : ""})
      </Typography>

      <Typography variant="body2" color="textSecondary" gutterBottom>
        Paso {activeStep + 1} de {PURPOSES.length}
      </Typography>

      <Stepper activeStep={activeStep} alternativeLabel>
        {PURPOSES.map((label, index) => (
          <Step key={label}>
            <StepLabel
              onClick={() => setActiveStep(index)}
              style={{ cursor: "pointer" }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box mt={3}>
        <Typography variant="h6" gutterBottom>
          {meta.title}
        </Typography>

        <Typography variant="body2" color="textSecondary" paragraph>
          {meta.description}
        </Typography>

        <Paper variant="outlined" style={{ padding: 16, marginBottom: 16 }}>
          <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
            Cómo crear esta plantilla en Twilio
          </Typography>

          <ol style={{ paddingLeft: 18, lineHeight: 1.6 }}>
            <li>
              <Typography variant="body2">
                Entra a{" "}
                <Link href={TWILIO_TEMPLATE_BUILDER_URL} target="_blank" rel="noopener noreferrer">
                  Twilio Content Template Builder
                </Link>
                .
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Crea una nueva plantilla con nombre sugerido: <strong>{meta.suggestedTemplateName}</strong>.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Pega el texto sugerido en el campo Body:
              </Typography>
              <Paper variant="outlined" style={{ padding: 12, marginTop: 12 }}>
                <Typography
                  variant="body2"
                  style={{ whiteSpace: "pre-line", fontFamily: "monospace", marginBottom: 8 }}
                >
                  {meta.suggestion}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigator.clipboard.writeText(meta.suggestion)}
                >
                  Copiar
                </Button>
              </Paper>
            </li>
            <li>
              <Typography variant="body2">
                Completa Sample Data con valores de ejemplo:
              </Typography>
              <Box mt={1} mb={1} style={{ fontFamily: "monospace" }}>
                {Object.entries(meta.sampleData).map(([k, v]) => (
                  <Box key={k} display="flex" alignItems="center" mb={0.5}>
                    <Typography variant="body2" style={{ marginRight: 8 }}>
                      {k} : {v}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigator.clipboard.writeText(v)}
                    >
                      Copiar
                    </Button>
                  </Box>
                ))}
              </Box>
            </li>
          </ol>
        </Paper>

        <details style={{ marginBottom: 12 }}>
          <summary style={{ cursor: "pointer", fontWeight: 600 }}>
            Variables requeridas ({meta.variables.length})
          </summary>
          <Box mt={1}>
            {meta.variables.map((v) => (
              <Typography key={v.key} variant="body2">
                <strong>{v.key}</strong> : {v.meaning}
              </Typography>
            ))}
          </Box>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            Asegurate de que el body en Twilio tenga la misma cantidad de placeholders.
          </Typography>
        </details>

        <TextField
          label="Contenido HX (Template Name aprobado)"
          variant="outlined"
          fullWidth
          value={hxTemplate}
          onChange={(e) => setHxTemplate(e.target.value)}
          disabled={loading}
          helperText="Pega aquí el Template Name aprobado por Twilio."
          style={{ marginBottom: 8 }}
        />

        <Typography variant="body2" color="textSecondary">
          {templatesMap[currentPurpose] ? "Plantilla actual detectada para este propósito." : "No se detectó plantilla guardada aún."}
        </Typography>
      </Box>

      <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
        <Button onClick={handleBack} disabled={activeStep === 0 || loading}>
          Atrás
        </Button>

        <Box>
          {activeStep < PURPOSES.length - 1 ? (
            <Button
              onClick={handleSave}
              color="primary"
              variant="contained"
              disabled={loading || !hxTemplate}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? "Guardando..." : "Guardar y Siguiente"}
            </Button>
          ) : (
            <Button
              onClick={handleSave}
              color="primary"
              variant="contained"
              disabled={loading || !hxTemplate}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? "Guardando..." : "Finalizar"}
            </Button>
          )}
        </Box>
      </Box>

      <Box mt={2}>
        {!allTemplatesDefined && (
          <Typography variant="body2" color="error">
            Atención: faltan plantillas por configurar. Podés guardar paso a paso o completar todas y luego cerrar.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default TwilioTemplateWizard;
